import { useContext, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import { store } from 'components/Store';
import CreateConstituentContent from 'components/WritingAssistant/ui/CreateConstituentContent';
import CreditsButton from 'components/Dashboard/components/CreditsButton';
import CustomInstructionsPopup from 'components/WritingAssistant/ui/CustomInstructionsPopup';
import { useSuggestionFunctions } from 'components/WritingAssistant/hooks/useSuggestionFunctions';

const Create = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;

  const { remainingCredits } = state;
  const { titleValues, phrasesValues, createFunction, individualPage, sentPromptFromWritingAssistant } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const [isOpenCustomInstructionsPopup, setIsOpenCustomInstructionsPopup] = useState(false);

  const params = useParams();
  const location = useLocation();
  const { createPrompt, promptsPhrases, normalizeEditorValue } = useCreatePrompt();
  const { getResponseToConstituent } = useSuggestionFunctions();

  const { title: contentType } = params;

  const section = promptsPhrases[contentType];

  const reviewSuggestionFunction = async (e) => {
    const influencePage = location?.state?.influencePage;
    if (normalizeEditorValue(titleValues?.title) === '') {
      createNotification('danger', 'Please enter a subject');
    } else {
      let prompt = createPrompt({ contentType, title: titleValues?.title, phrases: phrasesValues?.phrases });
      if (individualPage && !influencePage) {
        sentPromptFromWritingAssistant({ prompt });
      } else {
        setLoadingState(true);
        setIsLoading(true);
        let suggestion = await getResponseToConstituent({ prompt });
        if (suggestion) {
          await createFunction({ prompt, body: suggestion?.output?.[0]?.content?.[0]?.text });
        }
      }
    }
  };
  return (
    <>
      <div className='flex-grow-1'>
        <CustomScrollbar className={'simple-scrollbar'} maximalThumbYSize={100}>
          {!individualPage && (
            <div
              className={`bg-white general-subnav inbox-items-bar trends-subnav-bar overflow-auto writing-assistant-bar`}
              style={{ minHeight: '147px' }}
            >
              <div className='mx-auto main-content-wrapper px-lg-5 px-3 pb-2'>
                <>
                  <h3 className='title-h4-bold specific-bottom-margin-20'>Respond to a constituent</h3>
                  <div className='flex-centered gap-15 pb-2'>
                    <Link
                      to='/ai-assistant'
                      className='action-button general-button rounded-pill specific-left-padding-20 pr-4 py-2 text-center'
                      style={{ minWidth: '223px' }}
                    >
                      <span className='icon-long-arrow-left paragraph-p5 mr-2' /> Back to AI assistant
                    </Link>
                    {remainingCredits >= 0 && (
                      <>
                        <CreditsButton
                          type={'AI'}
                          subtype={'assistantPolicymaker'}
                          className={
                            'rounded-pill border general-button py-2 specific-left-padding-46 pr-4 hovered-button assistant-policymakers-credits-button'
                          }
                        />
                      </>
                    )}
                    <button
                      className='hovered-button rounded-pill border general-button  py-2 px-4'
                      onClick={() => setIsOpenCustomInstructionsPopup(true)}
                    >
                      Your team’s custom instructions
                      <span
                        data-tooltip-id='general-tooltip'
                        data-tooltip-class-name='pricing-feature-tooltip feed-tooltip billing-tooltip'
                        data-tooltip-content='Custom instructions are factored into every response your team generates using the AI assistant. You can also add further instructions for an individual response using the box below.'
                        data-tooltip-place='bottom'
                        className='icon-info d-inline-block-centered paragraph-p3 ml-2 light-50-text'
                      />
                    </button>
                  </div>
                </>
              </div>
            </div>
          )}
          <div className='mx-auto main-content-wrapper px-lg-5 px-3 pt-3 pt-lg-4'>
            <CreateConstituentContent
              titleValues={titleValues}
              section={section}
              isLoading={isLoading}
              loadingState={loadingState}
              phrasesValues={phrasesValues}
              reviewSuggestionFunction={reviewSuggestionFunction}
            />
          </div>
        </CustomScrollbar>
      </div>
      <CustomInstructionsPopup
        isOpenState={{ isOpen: isOpenCustomInstructionsPopup, setIsOpen: setIsOpenCustomInstructionsPopup }}
      />
    </>
  );
};

const useCreatePrompt = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team } = state;

  const { aiAssistantConstituentResponseInstructions } = team ?? {};

  const phraseEnd = `Return only the final letter text. Do not include any introductory phrases, explanations, headings, commentary, or metadata. Use UK spelling and dates (e.g., 7 January 2026). Avoid jargon, acronyms, and party-political point-scoring. Use careful phrasing like “I will raise this with…” or “I have asked…” If a casework action is needed, ask for the minimum required details (full name, address/postcode, reference numbers, consent to contact the body). If the issue is for the local council, signpost clearly and offer to write in support where appropriate. Never promise outcomes. Never request sensitive personal data unless necessary, and never include it back in the email. Do not include legal advice, medical advice, or commentary on ongoing court cases. Do not mention internal processes, staff names, or that the response was drafted with AI.`;

  const promptsPhrases = {
    'response-to-a-constituent': {
      subject:
        'I am an MP in the UK and I would like to write a response to a constituent who contacted me with the following message:',
      button: 'response',
      placeHolderSubject: 'Message from constituent',
      placeHolderPhrases: [
        'E.g. Address the constituent as Ms Smith',
        'E.g. Thank the constituent for...',
        'E.g. Make reference to the fact that...',
      ],
    },
  };

  const createPrompt = ({ contentType, title, phrases }) => {
    const section = promptsPhrases[contentType];
    const teamResponseInstructionsValidation = normalizeEditorValue(aiAssistantConstituentResponseInstructions) !== '';
    const instructionsForResponse = `${phrases?.filter((item) => normalizeEditorValue(item) !== '').join('; ')}`;
    const instructionsValidation = normalizeEditorValue(instructionsForResponse) !== '';

    return `${section?.subject} ${title}. ${phraseEnd} ${teamResponseInstructionsValidation ? `Incorporate these organisation level instructions when producing this letter: ${aiAssistantConstituentResponseInstructions}.` : ''} ${`${instructionsValidation ? `For this response only, incorporate these response-specific instructions: ${instructionsForResponse}.` : ''}`} ${teamResponseInstructionsValidation && instructionsValidation ? 'Any response-specific instructions should override any organisation level instructions should they conflict.' : ''}`;
  };

  const normalizeEditorValue = (value) => {
    if (!value) return '';
    return value
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  };

  return { createPrompt, promptsPhrases, normalizeEditorValue, phraseEnd };
};

export { useCreatePrompt };
export default Create;
